/**
 * UI utilities for Comments component
 * Handles common styling patterns and UI component helpers
 */

/**
 * Gets common button styling based on theme and state
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @param {string} variant - Button variant ('default', 'highlight', 'danger')
 * @returns {Object} Style object for the button
 */
export const getButtonStyles = (isDarkMode, variant = 'default') => {
  const baseStyles = {
    borderWidth: "thick",
    borderRadius: "15px",
    padding: "0.5rem 1rem"
  };

  switch (variant) {
    case 'highlight':
      return {
        ...baseStyles,
        border: isDarkMode ? "3px solid #fbbf24" : "3px solid #d97706",
        boxShadow: isDarkMode
          ? '0 0 15px rgba(251, 191, 36, 0.4)'
          : '0 0 15px rgba(217, 119, 6, 0.4)'
      };
    
    case 'danger':
      return {
        ...baseStyles,
        border: isDarkMode ? "3px solid #f56565" : "3px solid #dc2626",
        boxShadow: isDarkMode
          ? '0 0 15px rgba(245, 101, 101, 0.4)'
          : '0 0 15px rgba(220, 38, 38, 0.4)'
      };
    
    default:
      return {
        ...baseStyles,
        border: isDarkMode ? "3px solid white" : "3px solid black"
      };
  }
};

/**
 * Gets form control styling based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for form controls
 */
export const getFormControlStyles = (isDarkMode) => ({
  backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
  color: isDarkMode ? '#e2e8f0' : '#2d3748',
  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
});

/**
 * Gets select dropdown styling based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for select dropdowns
 */
export const getSelectStyles = (isDarkMode) => ({
  borderWidth: "thick",
  border: isDarkMode ? "3px solid white" : "3px solid black",
  borderRadius: "15px",
  backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
  color: isDarkMode ? '#e2e8f0' : '#2d3748'
});

/**
 * Gets pagination item styling based on theme and active state
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @param {boolean} isActive - Whether the pagination item is active
 * @returns {Object} Style object for pagination items
 */
export const getPaginationItemStyles = (isDarkMode, isActive) => ({
  backgroundColor: isActive
    ? (isDarkMode ? '#4a5568' : '#e2e8f0')
    : (isDarkMode ? '#2d3748' : '#ffffff'),
  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
  color: isDarkMode ? '#e2e8f0' : '#2d3748'
});

/**
 * Gets modal styling based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for modals
 */
export const getModalStyles = (isDarkMode) => ({
  backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
  borderColor: isDarkMode ? '#4a5568' : '#e2e8f0'
});

/**
 * Gets comment highlight styling for new comments
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for new comment highlights
 */
export const getNewCommentHighlightStyles = (isDarkMode) => ({
  border: `5px solid ${isDarkMode ? '#68d391' : '#38a169'}`,
  boxShadow: isDarkMode 
    ? '0 0 20px rgba(104, 211, 145, 0.3)' 
    : '0 0 20px rgba(56, 161, 105, 0.3)'
});

/**
 * Gets text color class based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @param {string} variant - Text variant ('primary', 'secondary', 'muted')
 * @returns {string} CSS class for text color
 */
export const getTextColorClass = (isDarkMode, variant = 'primary') => {
  if (isDarkMode) {
    switch (variant) {
      case 'secondary':
        return 'text-white-50';
      case 'muted':
        return 'text-white-75';
      default:
        return 'text-white';
    }
  } else {
    switch (variant) {
      case 'secondary':
        return 'text-muted';
      case 'muted':
        return 'text-muted';
      default:
        return '';
    }
  }
};
